import { useEffect, useRef, useState } from "react";

/**
 * useIdleTimer
 * Detects user inactivity (no mousemove / keydown / click / scroll / touchstart)
 * and fires `onIdle` once the threshold is crossed.
 *
 * @param {Object} opts
 * @param {number} opts.timeout       Idle threshold in ms (e.g. 10*60*1000)
 * @param {boolean} opts.enabled      When false, the timer is disabled (no listeners)
 * @param {Function} [opts.onIdle]    Optional callback when idle threshold is reached
 * @returns {{ isIdle: boolean, reset: () => void }}
 */
export function useIdleTimer({ timeout = 10 * 60 * 1000, enabled = true, onIdle } = {}) {
  const [isIdle, setIsIdle] = useState(false);
  const timerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const onIdleRef = useRef(onIdle);

  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  useEffect(() => {
    if (!enabled) {
      if (timerRef.current) clearTimeout(timerRef.current);
      setIsIdle(false);
      return undefined;
    }

    const armTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setIsIdle(true);
        if (typeof onIdleRef.current === "function") {
          try { onIdleRef.current(); } catch (e) { /* noop */ }
        }
      }, timeout);
    };

    const onActivity = () => {
      // Throttle: ignore activity bursts within 1s window
      const now = Date.now();
      if (now - lastActivityRef.current < 1000) return;
      lastActivityRef.current = now;
      setIsIdle(false);
      armTimer();
    };

    const events = ["mousemove", "mousedown", "keydown", "click", "scroll", "touchstart", "wheel"];
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    armTimer();

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [timeout, enabled]);

  const reset = () => {
    lastActivityRef.current = Date.now();
    setIsIdle(false);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (enabled) {
      timerRef.current = setTimeout(() => {
        setIsIdle(true);
        if (typeof onIdleRef.current === "function") {
          try { onIdleRef.current(); } catch (e) { /* noop */ }
        }
      }, timeout);
    }
  };

  return { isIdle, reset };
}

export default useIdleTimer;
